import React, { useState } from "react"
import { Box, Button, Hidden, makeStyles, Typography, useMediaQuery, useTheme } from "@material-ui/core"
import { ActionCard, ActionCardInfo } from "../ActionCards"
import { AuthorTools } from "../Author"
import { Avatar } from "../Avatar"
import { ColumnBox, FlexBox, RowBox } from "../Boxes"
import { Icon, AdornmentIcon } from "../Icon"
import {
  TRIGGER_SCHEDULE_SOURCE_OUTPUT_TYPE_LABELS,
  TRIGGER_TARGET_TYPE,
  TRIGGER_TARGET_TYPE_LABELS,
  TRIGGER_TYPE,
  TRIGGER_NOTIFY_METHOD_LABELS,
  TRIGGER_NOTIFY_METHOD,
} from "../../data"
import { FormatDateTimeCompact } from "../Format"
import HumanReadableCron from "./HumanReadableCron"
import { formatEventLabel } from "../../utils/events"
import { Assignees } from "../Assignees"
import { toId } from "../../utils"
import {
  useMutationDeleteTrigger,
  useMutationPauseTrigger,
  useMutationResumeTrigger,
  useMutationTestTrigger,
} from "../../data/triggers/useMutationTrigger"
import { LoadingSpinner } from "../LoadingSpinner"
import { useSnackbar } from "../SnackbarProvider"

const { NOTIFY_USERS, NOTIFY_EMAILS, INTEGRATION } = TRIGGER_TARGET_TYPE
const { EMAIL, PUSH } = TRIGGER_NOTIFY_METHOD

const ActionCardFooterInfo = ({ title, value, icon, mr }) => (
  <RowBox mr={mr}>
    <Hidden mdUp>
      <ActionCardInfo>
        <AdornmentIcon
          name={icon}
          tooltip={
            <>
              {title}: {value}
            </>
          }
        />
      </ActionCardInfo>
    </Hidden>
    <Hidden smDown>
      <AdornmentIcon name={icon} tooltip={title} />
      <Box ml={1} mr={0}>
        {value}
      </Box>
    </Hidden>
  </RowBox>
)

const useFooterStyles = makeStyles(() => ({
  pausedAvatar: {
    width: 24,
    height: 24,
  },
}))

const Footer = ({ trigger }) => {
  const classes = useFooterStyles()
  const theme = useTheme()
  const small = useMediaQuery(theme.breakpoints.down("sm"))
  const snackbar = useSnackbar()
  const [testTrigger, { loading: testLoading }] = useMutationTestTrigger()
  const [testing, setTesting] = useState(false)
  const { targetType, notifyUsersTarget, pausedAt, pausedBy, createdAt, updatedAt } = trigger

  const handleTest = async () => {
    if (!testing) {
      setTesting(true)
      return
    }
    await testTrigger({ variables: { id: toId(trigger) } })
    snackbar.showMessage({ message: "Test notification sent", icon: <Icon name="mail" /> })
    setTesting(false)
  }

  const hasTest =
    targetType === NOTIFY_EMAILS ||
    (targetType === NOTIFY_USERS && notifyUsersTarget?.methods.some((method) => [EMAIL, PUSH].includes(method)))

  return (
    <>
      {hasTest && !pausedAt && (
        <Button variant="outlined" size="small" onClick={handleTest} disabled={testLoading}>
          {!testing && !testLoading && <>Test</>}
          {testing && !testLoading && <>Confirm test?</>}
          {testLoading && <LoadingSpinner size={24} delay={false} />}
        </Button>
      )}

      {Boolean(pausedAt) && (
        <RowBox>
          <ActionCardFooterInfo
            title="Currently paused"
            value={
              <>
                Paused <FormatDateTimeCompact value={pausedAt} title="Paused" />
              </>
            }
            icon="paused"
            mr={1}
          />
          {pausedBy && <Avatar {...pausedBy} className={classes.pausedAvatar} />}
        </RowBox>
      )}

      <RowBox ml="auto">
        <ActionCardFooterInfo
          title="Created"
          value={<FormatDateTimeCompact value={createdAt} title="Created" />}
          icon="date"
          mr={small ? 1 : 3}
        />
        <ActionCardFooterInfo
          title="Last activity"
          value={<FormatDateTimeCompact value={updatedAt} title="Last activity" />}
          icon="last-activity"
          mr={small ? -1 : 0}
        />
      </RowBox>
    </>
  )
}

const ScheduleTriggerTargetSummary = ({ trigger }) => {
  const { scheduleSource, targetType, notifyUsersTarget, notifyEmailsTarget, integrationTarget } = trigger

  switch (targetType) {
    case NOTIFY_USERS:
      return (
        <>
          <Box mb={0.5}>
            {TRIGGER_TARGET_TYPE_LABELS[targetType]}
            {": "}
            <Assignees users={notifyUsersTarget.users} groups={notifyUsersTarget.groups} />
          </Box>
          <Box mb={0.5}>
            {TRIGGER_SCHEDULE_SOURCE_OUTPUT_TYPE_LABELS[notifyUsersTarget.output]} for{" "}
            {scheduleSource.locations?.map((item) => item.name).join(", ") || "all locations"}
          </Box>
          <Box>{notifyUsersTarget.methods.map((method) => TRIGGER_NOTIFY_METHOD_LABELS[method]).join(", ")}</Box>
        </>
      )
    case NOTIFY_EMAILS:
      return (
        <>
          <Box mb={0.5}>
            {TRIGGER_TARGET_TYPE_LABELS[targetType]}
            {": "}
            {notifyEmailsTarget.emails.join(", ")}
          </Box>
          <Box>
            {TRIGGER_SCHEDULE_SOURCE_OUTPUT_TYPE_LABELS[notifyEmailsTarget.output]} for{" "}
            {scheduleSource.locations?.map((item) => item.name).join(", ") || "all locations"}
          </Box>
        </>
      )
    case INTEGRATION:
      return <Box>{toId(integrationTarget)}</Box>
    default:
      return null
  }
}

const useScheduleStyles = makeStyles((theme) => ({
  title: {
    fontSize: 16,
    lineHeight: "20px",
    fontWeight: "500",
    color: theme.palette.text.main,
    marginBottom: theme.spacing(0.5),
  },
}))

const ScheduleTriggerActionCard = ({ trigger, onEdit, onDelete, detail, ...rest }) => {
  const classes = useScheduleStyles()
  const [pauseTrigger, { loading: pauseLoading }] = useMutationPauseTrigger()
  const [resumeTrigger, { loading: resumeLoading }] = useMutationResumeTrigger()
  const [deleteTrigger, { loading: deleteLoading }] = useMutationDeleteTrigger(trigger.__typename)
  const {
    id,
    scheduleSource: { schedules, timeZone },
    pausedAt,
  } = trigger

  const handleEdit = () => {
    if (onEdit) onEdit(trigger)
  }
  const handleTogglePause = async () => {
    if (trigger.pausedAt) {
      await resumeTrigger({ variables: { id: toId(trigger) } })
    } else {
      await pauseTrigger({ variables: { id: toId(trigger) } })
    }
  }
  const handleDelete = async () => {
    await deleteTrigger({ variables: { id: toId(trigger) } })
  }

  const loading = pauseLoading || resumeLoading || deleteLoading

  return (
    <ActionCard
      key={id}
      title={
        <Typography variant="h2" className={classes.title}>
          <HumanReadableCron expression={schedules[0]} timeZone={timeZone} className={classes.title} />
        </Typography>
      }
      stub={
        <ColumnBox mr={1} alignSelf="flex-start">
          <Icon name="trigger-scheduled" />
        </ColumnBox>
      }
      detail={<ScheduleTriggerTargetSummary trigger={trigger} />}
      cursor={null}
      rightChildren={
        <FlexBox alignSelf="flex-start">
          <AuthorTools
            item={trigger}
            author={toId(trigger.author, true)}
            admins={{
              edit: ["trigger_update_all", "trigger_update_locations"],
              delete: ["trigger_update_all", "trigger_update_locations"],
            }}
            disableLabel={pausedAt ? "Unpause trigger" : "Pause trigger"}
            onEdit={handleEdit}
            subject="trigger"
            onDisable={handleTogglePause}
            onDelete={handleDelete}
            loading={loading}
          />
        </FlexBox>
      }
      footer={<Footer trigger={trigger} />}
      {...rest}
    />
  )
}

const EventTriggerTargetSummary = ({ trigger }) => {
  const { eventSource, targetType, notifyUsersTarget, notifyEmailsTarget, integrationTarget } = trigger

  switch (targetType) {
    case NOTIFY_USERS:
      return (
        <>
          <Box mb={0.5}>
            {eventSource.process?.name}, for{" "}
            {eventSource.locations?.map((item) => item.name).join(", ") || "all locations"}
          </Box>
          <Box mb={0.5}>
            {TRIGGER_TARGET_TYPE_LABELS[targetType]}
            {": "}
            <Assignees users={notifyUsersTarget.users} groups={notifyUsersTarget.groups} />
          </Box>
          <Box>{notifyUsersTarget.methods.map((method) => TRIGGER_NOTIFY_METHOD_LABELS[method]).join(", ")}</Box>
        </>
      )
    case NOTIFY_EMAILS:
      return (
        <>
          <Box>
            {eventSource.process?.name}, for{" "}
            {eventSource.locations?.map((item) => item.name).join(", ") || "all locations"}
          </Box>
          <Box mb={0.5}>
            {TRIGGER_TARGET_TYPE_LABELS[targetType]}
            {": "}
            {notifyEmailsTarget.emails.join(", ")}
          </Box>
        </>
      )
    case INTEGRATION:
      return <Box>{toId(integrationTarget)}</Box>
    default:
      return null
  }
}

const EventTriggerActionCard = ({ trigger, onEdit, onDelete, detail, ...rest }) => {
  const [pauseTrigger, { loading: pauseLoading }] = useMutationPauseTrigger()
  const [resumeTrigger, { loading: resumeLoading }] = useMutationResumeTrigger()
  const [deleteTrigger, { loading: deleteLoading }] = useMutationDeleteTrigger(trigger.__typename)
  const {
    id,
    eventSource: { events },
    pausedAt,
  } = trigger

  const handleEdit = () => {
    onEdit && onEdit(trigger)
  }

  const handleTogglePause = async () => {
    if (trigger.pausedAt) {
      await resumeTrigger({ variables: { id: toId(trigger) } })
    } else {
      await pauseTrigger({ variables: { id: toId(trigger) } })
    }
  }
  const handleDelete = async () => {
    await deleteTrigger({ variables: { id: toId(trigger) } })
  }

  const loading = pauseLoading || resumeLoading || deleteLoading

  return (
    <ActionCard
      key={id}
      title={events.map((event) => formatEventLabel(event)).join(", ")}
      stub={
        <ColumnBox mr={1} alignSelf="flex-start">
          <Icon name="trigger-event" />
        </ColumnBox>
      }
      detail={<EventTriggerTargetSummary trigger={trigger} />}
      cursor={null}
      rightChildren={
        <FlexBox alignSelf="flex-start">
          <AuthorTools
            item={trigger}
            admins={{
              edit: ["trigger_update_all", "trigger_update_locations"],
              disable: ["trigger_update_all", "trigger_update_locations"],
              delete: ["trigger_update_all", "trigger_update_locations"],
            }}
            disableLabel={pausedAt ? "Unpause trigger" : "Pause trigger"}
            onEdit={handleEdit}
            subject="trigger"
            onDisable={handleTogglePause}
            onDelete={handleDelete}
            loading={loading}
          />
        </FlexBox>
      }
      footer={<Footer trigger={trigger} />}
      {...rest}
    />
  )
}

const TriggerActionCard = ({ trigger, onEdit, onDelete, ...props }) => {
  const type = trigger.__typename === "EventTrigger" ? TRIGGER_TYPE.EVENT : TRIGGER_TYPE.SCHEDULE

  const handleEdit = () => {
    onEdit && onEdit(trigger)
  }

  switch (type) {
    case TRIGGER_TYPE.EVENT:
      return <EventTriggerActionCard trigger={trigger} onEdit={handleEdit} {...props} />
    case TRIGGER_TYPE.SCHEDULE:
      return <ScheduleTriggerActionCard trigger={trigger} onEdit={handleEdit} {...props} />
    default:
      throw new Error("Unknown trigger type")
  }
}

export default TriggerActionCard
